import { useState } from 'react';
import { Play, Loader2, ChevronDown, RefreshCw, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Dialog, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { cn } from '../lib/utils';
import { api } from '../api/feedy';

// Shared "Run pipeline" control used in the header and on the Schedule page.
// Clicking the caret opens a small menu to choose Sync+Generate (default) vs
// Generate-only. While the latest run is 'running' the button shows a spinner
// and is disabled. `onStarted` lets the parent kick off its live poll.
//
// Both actions are pipeline-WIDE (every source / all 43 feeds), so a confirm
// step guards against the reflex click — to test a single feed you want its
// own Generate / Preview, not a full run.
export default function RunPipelineButton({ running = false, onStarted, size = 'sm', className }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // { tone: 'ok'|'warn'|'error', text }
  const [pending, setPending] = useState(null); // { sync, generate } awaiting confirm

  const trigger = async ({ sync, generate }) => {
    setBusy(true);
    setMsg(null);
    try {
      const run = await api.pipelineRuns.run({ sync, generate });
      setMsg({ tone: 'ok', text: sync ? 'Run started — syncing & generating' : 'Run started — generating' });
      onStarted?.(run);
    } catch (err) {
      if (err?.response?.status === 409) {
        setMsg({ tone: 'warn', text: 'A run is already in progress' });
        onStarted?.(err.response.data);
      } else {
        setMsg({ tone: 'error', text: 'Could not start the run' });
      }
    } finally {
      setBusy(false);
      setTimeout(() => setMsg(null), 6000);
    }
  };

  const disabled = busy || running;
  const Icon = running ? RefreshCw : Play;

  return (
    <div className="flex items-center gap-2">
      {msg && (
        <span
          className={cn(
            'hidden whitespace-nowrap text-[12px] font-medium sm:inline',
            msg.tone === 'ok' && 'text-emerald-600',
            msg.tone === 'warn' && 'text-amber-600',
            msg.tone === 'error' && 'text-rose-600',
          )}
        >
          {msg.text}
        </span>
      )}
      <div className="inline-flex">
        <Button
          size={size}
          className={cn('gap-1.5 rounded-r-none', className)}
          disabled={disabled}
          onClick={() => setPending({ sync: true, generate: true })}
          title="Sync sources, then generate all feeds"
        >
          {busy || running
            ? <Loader2 className="h-4 w-4 animate-spin" />
            : <Icon className="h-4 w-4" />}
          {running ? 'Running…' : 'Run pipeline'}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size={size}
              className="rounded-l-none border-l border-primary-foreground/20 px-2"
              disabled={disabled}
              aria-label="Run options"
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            <DropdownMenuLabel className="text-[12px] font-medium text-muted-foreground">Choose what to run</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="gap-2" onSelect={() => setPending({ sync: true, generate: true })}>
              <Play className="h-3.5 w-3.5 shrink-0" />
              <div>
                <p className="text-[13px] font-medium">Sync &amp; generate</p>
                <p className="text-[11px] text-muted-foreground">Pull sources, then build every feed</p>
              </div>
            </DropdownMenuItem>
            <DropdownMenuItem className="gap-2" onSelect={() => setPending({ sync: false, generate: true })}>
              <RefreshCw className="h-3.5 w-3.5 shrink-0" />
              <div>
                <p className="text-[13px] font-medium">Generate only</p>
                <p className="text-[11px] text-muted-foreground">Rebuild feeds from current data</p>
              </div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <Dialog open={!!pending} onClose={() => setPending(null)}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            {pending?.sync ? 'Run the full pipeline?' : 'Rebuild every feed?'}
          </DialogTitle>
          <DialogDescription>
            {pending?.sync
              ? 'This syncs every source (incl. the ~8-min Shopify pull) and then rebuilds all feeds. It affects the whole catalog, not one feed.'
              : 'This rebuilds all feeds from the current data. It affects every feed, not one.'}
          </DialogDescription>
        </DialogHeader>
        <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          Just testing a single feed? Open it and use <span className="font-medium text-foreground">Generate</span> or
          the <span className="font-medium text-foreground">Preview</span> tab instead — no full run needed.
        </p>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => setPending(null)}>Cancel</Button>
          <Button
            size="sm"
            className="gap-1.5"
            onClick={() => { const p = pending; setPending(null); trigger(p); }}
          >
            <Play className="h-4 w-4" /> {pending?.sync ? 'Run pipeline' : 'Generate all'}
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}
